import { Button } from "@shared/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from "@shared/ui/dropdown-menu";
import { useNavigate } from "react-router-dom";
import { Banknote, FileText, MessageSquare, Mail, AlertCircle, ArrowRightLeft, Unplug, Plus } from "lucide-react";
import { CommunicationDialog } from "@features/cx/shared/components/CommunicationDialog";
import { useState } from "react";
import { Consumer } from "@features/cx/consumerManagement/types";
import { useChannelTemplates, useChannelType, useSendCommunication } from "@features/cx/consumerManagement/hooks";
import { logEvent } from "@shared/analytics/analytics";

interface QuickActionMenuProps {
  consumerId: string | undefined;
  remoteUtilityId: number;
  consumerData: Consumer;
}

export function QuickActionMenu({
  consumerId,
  remoteUtilityId,
  consumerData
}: QuickActionMenuProps) {
  const navigate = useNavigate();
  const [showCommunicationDialog, setShowCommunicationDialog] = useState(false);

const handleAction = (action: string) => {
    switch (action) {
      case 'complaint':
        // Navigate to new complaint page with consumer pre-selected
        logEvent('Quick Action Used - Complaint');
        navigate('/cx/complaints/create', { 
          state: { 
            prefilledConsumer: {
              accountNumber: `ACC-${consumerId}`,
              // In a real app, you'd pass the actual consumer data
              name: "Selected Consumer",
              address: "Consumer Address",
              phone: "(555) 123-4567",
              email: "consumer@example.com",
              customerType: "Residential",
              customerSince: "January 15, 2020"
            }
          }
        });
        break;
      case 'communicate':
        logEvent('Quick Action Used - Communicate');
        setShowCommunicationDialog(true);
        break;
      case 'service':
        logEvent('Quick Action Used - Request Service');
        navigate('/cx/services/create', { 
          state: { 
            prefilledConsumer: {
              accountNumber: `ACC-${consumerId}`,
              name: "Selected Consumer"
            }
          }
        });
        break;
      case 'payments':
        logEvent('Quick Action Used - Add Payments');
        navigate('/cx/payments/create', { 
          state: { 
            filterByConsumer: consumerId
          }
        });
        break;
      case 'bills':
        logEvent('Quick Action Used - Bills');
        navigate('/cx/payments', { 
          state: { 
            filterByConsumer: consumerId,
            showBills: true
          }
        });
        break;
      case 'disconnection':
        logEvent('Quick Action Used - Raise Disconnection');
        navigate('/cx/disconnections/create', { 
          state: { 
            prefilledConsumer: {
              accountNumber: `ACC-${consumerId}`,
              name: "Selected Consumer"
            }
          }
        });
        break;
      case 'transfer':
        logEvent('Quick Action Used - Raise Transfer');
        navigate('/cx/transfer/create', { 
          state: { 
            prefilledConsumer: {
              accountNumber: `ACC-${consumerId}`,
              name: "Selected Consumer"
            }
          }
        });
        break;
    }
  };

  return (
    <div className="flex items-center gap-2">
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button variant="outline" size="sm" className="flex items-center gap-1">
            <Plus className="h-4 w-4" />
            Quick Actions
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-48 bg-white z-50">
          <DropdownMenuLabel>Quick Actions</DropdownMenuLabel>
          <DropdownMenuSeparator />
          
          <DropdownMenuItem 
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => handleAction('complaint')}
          >
            <AlertCircle className="h-4 w-4" />
            <span>Register Complaint</span>
          </DropdownMenuItem>
          
          <DropdownMenuItem 
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => handleAction('communicate')}
          >
            <MessageSquare className="h-4 w-4" />
            <span>Communicate</span>
          </DropdownMenuItem>
          
          <DropdownMenuItem 
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => handleAction('service')}
          >
            <Mail className="h-4 w-4" />
            <span>Request Service</span>
          </DropdownMenuItem>
          
          <DropdownMenuSeparator />
          
          <DropdownMenuItem 
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => handleAction('payments')}
          >
            <Banknote className="h-4 w-4" />
            <span>Add Payment</span>
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          
          <DropdownMenuItem 
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => handleAction('disconnection')}
          >
            <Unplug className="h-4 w-4" />
            <span>Raise Disconnection</span>
          </DropdownMenuItem>
          
          <DropdownMenuItem 
            className="flex items-center gap-2 cursor-pointer"
            onClick={() => handleAction('transfer')}
          >
            <ArrowRightLeft className="h-4 w-4" />
            <span>Raise Transfer</span>
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>

      <CommunicationDialog
        isOpen={showCommunicationDialog}
        onClose={() => setShowCommunicationDialog(false)}
        consumerId={consumerId}
        remoteUtilityId={remoteUtilityId}
        email={consumerData?.email}
        phone={consumerData?.contactNumber}
        useChannelType={useChannelType}
        useChannelTemplates={useChannelTemplates}
        useSendCommunication={useSendCommunication}
        communicationType="consumerDetailCommunication"
      />
    </div>
  );
}
